import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LineChart } from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { Card, CardContent } from '../../../components/ui/card'

function fmt(n) {
  return Math.round(Number(n) || 0).toLocaleString('en-US')
}

// Chart of `balance_entries` over time. We sort ascending (oldest →
// newest) so the line flows naturally left-to-right; the existing
// "Balance history" list section uses descending order, which is
// good for reading the latest entry first but reads backwards in a
// time-series chart.
export function BalanceHistoryChart({ entries }) {
  const { t } = useTranslation()

  const data = useMemo(() => {
    if (!Array.isArray(entries) || entries.length < 2) return []
    return [...entries]
      .sort((a, b) => (a.as_of_date < b.as_of_date ? -1 : 1))
      .map((e) => ({
        date: e.as_of_date,
        balance: Math.round(Number(e.amount) || 0),
      }))
  }, [entries])

  // No chart if we only have one (or zero) data points — a line of
  // one dot is noise. The list section below it already covers the
  // single-entry case.
  if (data.length < 2) return null

  return (
    <section className="space-y-2 pt-2">
      <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5 px-1" dir="auto">
        <LineChart className="w-4 h-4 text-app-primary-500" />
        {t('finance.balanceChart.title')}
      </h2>
      <Card>
        <CardContent className="p-3">
          <div className="h-48 w-full" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="balanceHistFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--app-primary-500))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--app-primary-500))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10}
                  tickFormatter={(d) => (d ? d.slice(5) : '')} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10}
                  tickFormatter={(v) => fmt(v)} />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem',
                    fontSize: '0.75rem',
                  }}
                  formatter={(value) => [`${fmt(value)} SAR`, t('finance.balanceChart.balance')]}
                />
                <Area type="monotone" dataKey="balance"
                  stroke="hsl(var(--app-primary-500))" strokeWidth={2}
                  fill="url(#balanceHistFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </section>
  )
}
